import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { SbomProject, SbomComponent, SbomDocument, SecurityAssessment, SecurityAssessmentCreate, SecurityRequirement } from '../Models/sbom.model';

@Injectable({ providedIn: 'root' })
export class SbomService {
  private apiUrl = environment.apiBaseUrl + '/sbom';

  constructor(private http: HttpClient) { }

  // Projects
  getProjects(): Observable<SbomProject[]> {
    return this.http.get<SbomProject[]>(this.apiUrl);
  }

  getProject(id: number): Observable<SbomProject> {
    return this.http.get<SbomProject>(`${this.apiUrl}/${id}`);
  }

  createProject(project: Partial<SbomProject>): Observable<SbomProject> {
    return this.http.post<SbomProject>(this.apiUrl, project);
  }

  updateProject(id: number, project: Partial<SbomProject>): Observable<SbomProject> {
    return this.http.put<SbomProject>(`${this.apiUrl}/${id}`, project);
  }

  deleteProject(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // Components
  getComponents(projectId: number): Observable<SbomComponent[]> {
    return this.http.get<SbomComponent[]>(`${this.apiUrl}/${projectId}/components`);
  }

  addComponent(projectId: number, component: Partial<SbomComponent>): Observable<SbomComponent> {
    return this.http.post<SbomComponent>(`${this.apiUrl}/${projectId}/components`, component);
  }

  updateComponent(projectId: number, componentId: number, component: Partial<SbomComponent>): Observable<SbomComponent> {
    return this.http.put<SbomComponent>(`${this.apiUrl}/${projectId}/components/${componentId}`, component);
  }

  deleteComponent(projectId: number, componentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${projectId}/components/${componentId}`);
  }

  // Documents
  uploadDocument(projectId: number, file: File, description?: string, documentType?: string): Observable<SbomDocument> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (description) formData.append('description', description);
    if (documentType) formData.append('documentType', documentType);
    return this.http.post<SbomDocument>(`${this.apiUrl}/${projectId}/documents`, formData);
  }

  downloadDocument(projectId: number, documentId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${projectId}/documents/${documentId}/download`, {
      responseType: 'blob'
    });
  }

  getDocumentPreviewUrl(projectId: number, documentId: number): string {
    return `${this.apiUrl}/${projectId}/documents/${documentId}/preview`;
  }

  deleteDocument(projectId: number, documentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${projectId}/documents/${documentId}`);
  }

  // Customer Logo
  uploadCustomerLogo(customerId: number, file: File): Observable<{ logoPath: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<{ logoPath: string }>(`${this.apiUrl}/customer/${customerId}/logo`, formData);
  }

  deleteCustomerLogo(customerId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/customer/${customerId}/logo`);
  }

  getCustomerLogoUrl(logoPath: string): string {
    return `${this.apiUrl}/customer-logos/${logoPath}`;
  }

  // Export
  exportSbom(projectId: number): Observable<SbomProject> {
    return this.http.get<SbomProject>(`${this.apiUrl}/${projectId}/export`);
  }

  // Security Assessment
  getSecurityAssessment(projectId: number): Observable<SecurityAssessment> {
    return this.http.get<SecurityAssessment>(`${this.apiUrl}/${projectId}/security-assessment`);
  }

  saveSecurityAssessment(projectId: number, assessment: SecurityAssessmentCreate): Observable<SecurityAssessment> {
    return this.http.post<SecurityAssessment>(`${this.apiUrl}/${projectId}/security-assessment`, assessment);
  }

  toggleSecurityRequirement(requirementId: number): Observable<SecurityRequirement> {
    return this.http.put<SecurityRequirement>(`${this.apiUrl}/security-requirements/${requirementId}/toggle`, {});
  }
}
